import { LocalDate } from 'js-joda';
import {
  RequestableState,
  createRequestableState,
  handleDone,
  handleFailed,
  handleStarting,
} from '@tock/shared/state/requestableState';
import { computeSavedEntityState } from 'state/utils/computeSavedEntityState';
import { dashboard } from 'types';
import { find, isEmpty } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';
import { payloadToJSON } from '@tock/coretypes';
import { todayBusinessDay } from '@tock/shared/utils/time';
import AppConstants from 'constants/AppConstants';

interface DayNotesSavedState {
  data?: dashboard.BusinessDayNote;
}

interface SavedState {
  [date: string]: DayNotesSavedState;
}

interface DayNotesState {
  data?: dashboard.BusinessDayNote;
  savedStateKeys: string[];
  savedState: SavedState;
  isRequestInProgress: boolean;
  selectedDate?: LocalDate;
  reservationDayNotes: RequestableState<dashboard.BusinessDayNote>; //for a changing date in reservations separate from calendar
}

export const initialState: DayNotesState = {
  data: undefined,
  savedStateKeys: [],
  savedState: {},
  isRequestInProgress: false,
  selectedDate: undefined,
  reservationDayNotes: createRequestableState<dashboard.BusinessDayNote>(),
};

export default function reducer(
  state: DayNotesState = initialState,
  action: BaseTockAction
): DayNotesState {
  const { type, payload } = action;

  switch (type) {
    case AppConstants.DAY_NOTES_FETCH_STARTING:
      return {
        ...state,
        data: undefined,
        isRequestInProgress: true,
      };
    case AppConstants.DAY_NOTES_SAVE_STARTING:
      return {
        ...state,
        isRequestInProgress: true,
      };
    case AppConstants.DAY_NOTES_FETCH_FAILED:
      return {
        ...state,
        data: undefined,
        isRequestInProgress: false,
      };
    case AppConstants.DAY_NOTES_SAVE_FAILED:
      return {
        ...state,
        isRequestInProgress: false,
      };
    case AppConstants.DAY_NOTES_FETCH_DONE:
    case AppConstants.DAY_NOTES_SAVE_DONE: {
      const dayNote = payloadToJSON<dashboard.BusinessDayNote>(payload);
      return {
        ...state,
        data: !isEmpty(dayNote) ? dayNote : undefined,
        isRequestInProgress: false,
      };
    }
    case AppConstants.CALENDAR_ENTITY_UPDATE: {
      const changeUpdates = payload as dashboard.CalendarDayEntityUpdates;
      if (oc(changeUpdates).update[0].type() === 'INIT') {
        return {
          ...state,
          data: oc(changeUpdates).update[0].dayNote() as dashboard.BusinessDayNote,
        };
      } else {
        const update = find(changeUpdates.update, (update) => update.type === 'REPLACE');
        if (update && update.dayNote) {
          return {
            ...state,
            data: oc(update).dayNote() as dashboard.BusinessDayNote,
          };
        }
        return state;
      }
    }
    case AppConstants.CALENDAR_ENTITY_SAVE_DATE: {
      const stateToSave: DayNotesSavedState = {
        data: state.data,
      };
      const newSavedState: SavedState = computeSavedEntityState<DayNotesSavedState>({
        newDate: payload,
        stateToSave,
        savedState: state.savedState,
        todayString: todayBusinessDay().toString(),
      });
      return {
        ...state,
        savedState: newSavedState,
      };
    }
    case AppConstants.CALENDAR_ENTITY_RESTORE_DATE: {
      const restoredState = state.savedState[payload];
      if (!restoredState) {
        return {
          ...state,
          data: initialState.data,
        };
      }
      return {
        ...state,
        data: restoredState.data,
      };
    }
    case AppConstants.DAY_NOTES_TOGGLE_MODAL: {
      return {
        ...state,
        selectedDate: payload ? payload.date : undefined,
      };
    }

    case AppConstants.DAY_NOTES_RESERVATION_STARTING: {
      return {
        ...state,
        reservationDayNotes: handleStarting(state.reservationDayNotes, action),
      };
    }
    case AppConstants.DAY_NOTES_RESERVATION_DONE: {
      return {
        ...state,
        reservationDayNotes: handleDone(state.reservationDayNotes, action),
      };
    }
    case AppConstants.DAY_NOTES_RESERVATION_FAILED: {
      return {
        ...state,
        reservationDayNotes: handleFailed(state.reservationDayNotes, action),
      };
    }

    default:
      return state;
  }
}
